'use client';

import type { RadioGroupItemProps } from '@magicschool/spellbook/components/RadioGroup/RadioGroupItem';
import { RadioGroupItem } from '@magicschool/spellbook/components/RadioGroup/RadioGroupItem';
import { useId } from '@magicschool/spellbook/hooks/useId';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import { useState } from 'react';

export type RadioGroupProps = React.ComponentProps<typeof RadioGroupPrimitive.Root> &
  VariantProps<typeof radioGroupVariants> & {
    /** The list of radio options to render. Each option maps to a `RadioGroupItem` with `value`, `label`, and optional `description`, `disabled`, `variant`, etc. */
    options: RadioGroupItemProps[];
    /** Controlled selected value. The `value` string of the currently selected option. */
    value?: string;
    /** Initial selected value for uncontrolled usage. */
    defaultValue?: string;
    /** Callback fired when the selection changes. Receives the `value` string of the newly selected option. */
    onCheckedChange?: (value: string) => void;
    /** Disables all radio items in the group. Individual options can also set their own `disabled`. */
    disabled?: boolean;
    /** Marks the group as required for form validation. */
    required?: boolean;
    /** Marks all radio items as invalid and sets `aria-invalid="true"` on the group. Use with form validation. */
    invalid?: boolean;
    /** The shared `name` attribute for all radio items, used for form submission. Auto-generated if not provided. */
    name?: string;
    /** The `id` of an external label element to associate with the group via `aria-labelledby`. */
    labelId?: string;
    /** When `true`, prevents selection changes while keeping items focusable. Useful for displaying results (e.g., quiz answers). */
    readOnly?: boolean;
  };

const radioGroupVariants = cva('flex gap-2 group w-full', {
  variants: {
    orientation: {
      horizontal: 'flex-row flex-wrap row-gap-4',
      vertical: 'flex-col',
    },
  },
});

/**
 * Single-selection radio button group for choosing one option from a visible list. Built on Radix UI RadioGroup. Renders `RadioGroupItem` components from an `options` array.
 *
 * **Note:** `RadioGroupItem` cannot be used as a standalone component outside of `RadioGroup`. Unlike `Checkbox` or `Switch`,
 * a single radio button has no meaning on its own — radio buttons are inherently about choosing one option from a set. The
 * Radix UI `RadioGroup.Item` primitive requires a parent `RadioGroup.Root` for selection state, keyboard navigation (arrow keys),
 * and `name` grouping. If you need a single binary choice, use `Checkbox` or `Switch` instead.
 *
 * ### When to use
 * - Choosing exactly one option from a short, visible list (e.g., export format, quiz answer, moderation level)
 * - When all options should be visible at once so the user can compare them
 * - Displaying read-only results with correct/incorrect feedback via the `variant` and `readOnly` props
 *
 * ### When NOT to use
 * - For selecting from a long list: use `Select` or `Autocomplete` instead (dropdown saves space)
 * - For multi-selection: use `CheckboxGroup` instead
 * - For binary on/off toggles: use `Switch` or `Checkbox` instead
 * - For action menus with radio behavior: use `DropdownMenu.RadioGroup` instead
 *
 * ### API
 * - `options`: Array of `RadioGroupItemProps`, each rendered as a `RadioGroupItem`. Supports per-option `label`, `value`, `description`, `disabled`, `variant`, and `readOnly`.
 * - `value` / `defaultValue`: Controlled or uncontrolled selected value as a single `value` string.
 * - `onCheckedChange`: Callback with the `value` string of the newly selected option.
 * - `orientation`: Layout direction: `'vertical'` (default) stacks items, `'horizontal'` arranges them in a wrapping row.
 * - `disabled`: Disables all items. Individual options can also be disabled independently.
 * - `invalid`: Sets `aria-invalid="true"` on the group and applies error borders to all items.
 * - `readOnly`: Prevents selection changes while keeping items focusable. Useful for displaying quiz results.
 * - `labelId`: Links an external label to the group via `aria-labelledby`.
 *
 * ### Visual states
 * - **Unselected**: Empty circle with border
 * - **Selected (default)**: Filled circle with primary color
 * - **Selected (checked)**: Green check icon, used for correct quiz answers
 * - **Selected (incorrect)**: Orange X icon, used for incorrect quiz answers
 * - **Disabled**: Muted border and background, cursor changes to `not-allowed`
 * - **Invalid**: Error-colored border via `aria-invalid`
 * - **Read-only**: No hover effects, cursor remains default, selection is locked
 *
 * ### Accessibility
 * - Uses Radix UI `RadioGroup.Root` which provides native radio group semantics and arrow-key navigation
 * - Use `labelId` to link an external label via `aria-labelledby` — without it, the group has no accessible name
 * - Each item's `label` is linked via `aria-labelledby`, `description` via `aria-describedby`
 * - Keyboard accessible: Arrow keys to navigate between options, Space to select
 *
 * See the [Checkbox documentation](?path=/docs/components-checkbox--docs) for a comparison table of all selection components.
 */
export const RadioGroup: React.FC<RadioGroupProps> = ({
  options = [],
  value: providedValue,
  defaultValue,
  onCheckedChange,
  labelId,
  disabled = false,
  invalid = false,
  name,
  className,
  orientation = 'vertical',
  readOnly = false,
  ...props
}: RadioGroupProps) => {
  const id = useId(name);

  // Internal state for uncontrolled usage
  const [internalValue, setInternalValue] = useState<string>(() => {
    if (defaultValue) {
      return defaultValue;
    }
    return '';
  });

  // Determine if component is controlled (has value prop) or uncontrolled
  const isControlled = providedValue !== undefined;
  const value = isControlled ? providedValue : internalValue;

  const handleCheckedChange = (newValue: string) => {
    if (disabled || readOnly) {
      return;
    }
    // Only update internal state if uncontrolled
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onCheckedChange?.(newValue);
  };

  return (
    <RadioGroupPrimitive.Root
      data-spellbook-name="radio-group"
      className={cn(radioGroupVariants({ orientation }), className)}
      aria-invalid={invalid}
      id={id}
      name={name || id}
      value={value}
      onValueChange={handleCheckedChange}
      {...(labelId && { 'aria-labelledby': labelId })}
      {...props}
    >
      {options.map((option) => (
        <RadioGroupItem
          key={option.value}
          id={option.id || option.value}
          value={option.value}
          label={option.label}
          description={option.description}
          disabled={disabled || option.disabled}
          checked={value === option.value}
          invalid={invalid || option.invalid}
          showLabel={option.showLabel}
          showDescription={option.showDescription}
          variant={option.variant}
          style={option.style}
          readOnly={readOnly || option.readOnly}
        />
      ))}
    </RadioGroupPrimitive.Root>
  );
};
